// ShowcasePage — Public achievement showcase / trophies display
// Layout: trophy case, pinned achievements, badge wall, recent unlocks

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TierPill } from '../../components/Brand';

interface Achievement {
  id: string;
  name: string;
  description: string;
  emoji: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  category: 'Trading' | 'Profit' | 'Challenge' | 'Social' | 'Bot';
  unlocked: boolean;
  progress?: number;
  pinned?: boolean;
  unlockedAt?: string;
}

const ACHIEVEMENTS: Achievement[] = [
  { id: 'a1', name: 'First Trade',         description: 'Open your first position',                 emoji: '🎯', rarity: 'common',    category: 'Trading',  unlocked: true,  unlockedAt: '2026-07-04' },
  { id: 'a2', name: 'Centurion',           description: 'Reach 100 lifetime trades',                  emoji: '💯', rarity: 'common',    category: 'Trading',  unlocked: true,  unlockedAt: '2026-07-09' },
  { id: 'a3', name: 'Hot Streak',          description: '5 winning trades in a row',                  emoji: '🔥', rarity: 'common',    category: 'Trading',  unlocked: true,  unlockedAt: '2026-07-22' },
  { id: 'a4', name: 'Profit Pioneer',      description: 'Reach $5,000 cumulative profit',             emoji: '🏆', rarity: 'epic',      category: 'Profit',   unlocked: false, progress: 58 },
  { id: 'a5', name: 'Disciplined',         description: '30 days without exceeding daily loss',      emoji: '🛡️', rarity: 'rare',      category: 'Trading',  unlocked: true,  unlockedAt: '2026-07-15' },
  { id: 'a6', name: 'Challenge Master',    description: 'Pass 3 challenges',                          emoji: '💎', rarity: 'epic',      category: 'Challenge',unlocked: false, progress: 33 },
  { id: 'a7', name: 'Funded',              description: 'Get funded for the first time',              emoji: '🚀', rarity: 'epic',      category: 'Challenge',unlocked: true,  unlockedAt: '2026-07-04', pinned: true },
  { id: 'a8', name: 'Bot Master',          description: 'Run 3 different bot strategies',            emoji: '🤖', rarity: 'rare',      category: 'Bot',      unlocked: false, progress: 33 },
  { id: 'a9', name: 'Top 100',             description: 'Reach the top 100 on the leaderboard',       emoji: '🏅', rarity: 'rare',      category: 'Social',   unlocked: true,  unlockedAt: '2026-07-18' },
  { id: 'a10', name: 'Whale',              description: 'Have $10,000 in your wallet',                emoji: '🐋', rarity: 'rare',      category: 'Profit',   unlocked: true,  unlockedAt: '2026-07-20', pinned: true },
  { id: 'a11', name: 'AI Whisperer',       description: 'Configure 10 custom bot parameters',         emoji: '🧠', rarity: 'legendary', category: 'Bot',      unlocked: false, progress: 60 },
  { id: 'a12', name: 'Risk Manager',       description: 'Use position calculator 50 times',           emoji: '🎲', rarity: 'common',    category: 'Trading',  unlocked: true,  unlockedAt: '2026-07-12' },
];

const RARITY_META: Record<Achievement['rarity'], { color: string; label: string; glow: string }> = {
  common:    { color: '#8E8E93', label: 'Common',    glow: 'rgba(142,142,147,0.2)' },
  rare:      { color: '#007AFF', label: 'Rare',      glow: 'rgba(0,122,255,0.25)' },
  epic:      { color: '#AF52DE', label: 'Epic',      glow: 'rgba(175,82,222,0.3)' },
  legendary: { color: '#FF9500', label: 'Legendary', glow: 'rgba(255,149,0,0.35)' },
};

const RARITY_FILTERS = ['All', 'common', 'rare', 'epic', 'legendary'] as const;
type RarityFilter = typeof RARITY_FILTERS[number];

export function ShowcasePage() {
  const nav = useNavigate();
  const [filter, setFilter] = useState<RarityFilter>('All');
  const [shareMode, setShareMode] = useState(false);

  const filtered = ACHIEVEMENTS.filter((a) => filter === 'All' || a.rarity === filter);
  const unlocked = ACHIEVEMENTS.filter((a) => a.unlocked);
  const pinned = unlocked.filter((a) => a.pinned);
  const recent = unlocked.filter((a) => a.unlockedAt).sort((a, b) => (b.unlockedAt || '').localeCompare(a.unlockedAt || '')).slice(0, 5);
  const completionPct = (unlocked.length / ACHIEVEMENTS.length) * 100;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Showcase</div>
        <button onClick={() => setShareMode(true)} data-testid="share-showcase" className="px-3 text-[15px] font-semibold text-tf-blue">Share</button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-3">
        <div className="ios26-hero text-center" style={{ background: 'linear-gradient(135deg, #FF9500 0%, #FF3B30 100%)', color: 'white' }}>
          <div className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.8)' }}>Trophy case</div>
          <div className="mt-1 text-[36px] font-bold tracking-tight">{unlocked.length} <span className="text-[18px] opacity-80">/ {ACHIEVEMENTS.length}</span></div>
          <div className="text-[12px]" style={{ color: 'rgba(255,255,255,0.85)' }}>{completionPct.toFixed(0)}% complete</div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/20">
            <div className="h-full rounded-full bg-white" style={{ width: completionPct + '%' }} />
          </div>
          <div className="mt-3 flex items-center justify-center gap-1.5">
            <TierPill tier="Professional" />
            <span className="rounded-full bg-white/20 backdrop-blur-md px-2 py-0.5 text-[9px] font-bold uppercase">Top 5%</span>
          </div>
        </div>
      </div>

      {/* Pinned */}
      {pinned.length > 0 && (
        <>
          <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">📌 Pinned</h2>
          <div className="mx-4 grid grid-cols-3 gap-2">
            {pinned.map((a) => <BadgeCard key={a.id} achievement={a} large />)}
          </div>
        </>
      )}

      {/* Recent */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">✨ Recently unlocked</h2>
      <div className="mx-4 grid grid-cols-5 gap-1.5">
        {recent.map((a) => <BadgeCard key={a.id} achievement={a} small />)}
      </div>

      {/* Rarity filter */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">All achievements</h2>
      <div className="px-4 flex items-center gap-1.5 overflow-x-auto">
        {RARITY_FILTERS.map((r) => (
          <button
            key={r}
            onClick={() => setFilter(r)}
            data-testid={`rarity-${r}`}
            className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
              filter === r ? 'bg-tf-text-1 text-white' : 'bg-white border border-tf-border text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >
            {r}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="mx-4 mt-3 grid grid-cols-4 gap-2" data-testid="badge-grid">
        {filtered.map((a) => <BadgeCard key={a.id} achievement={a} onClick={() => nav('/portal/achievement/' + a.id)} />)}
      </div>

      {/* Share modal */}
      {shareMode && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShareMode(false)}>
          <div className="ios26-sheet w-full max-w-[420px]" onClick={(e) => e.stopPropagation()}>
            <h2 className="px-4 pb-2 text-[20px] font-bold tracking-tight text-tf-text-1">Share your showcase</h2>
            <p className="px-4 pb-3 text-[13px] text-tf-text-3">Show off {unlocked.length} achievements and {completionPct.toFixed(0)}% completion</p>
            <div className="space-y-1.5">
              {['Copy link', 'Share to social', 'Download as image'].map((o) => (
                <button key={o} onClick={() => { alert(o); setShareMode(false); }} className="flex w-full items-center gap-3 rounded-[12px] px-3 py-2.5 active:bg-tf-surface-1" data-testid={`share-${o}`}>
                  <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue/10 text-[18px]">📤</div>
                  <span className="text-[14px] font-semibold text-tf-text-1">{o}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setShareMode(false)} className="mt-3 flex h-12 w-full items-center justify-center rounded-full bg-tf-surface-1 text-[15px] font-semibold text-tf-text-1 active:opacity-40">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function BadgeCard({ achievement: a, small, large, onClick }: { achievement: Achievement; small?: boolean; large?: boolean; onClick?: () => void }) {
  const r = RARITY_META[a.rarity];
  const size = small ? 'h-10 w-10 text-[18px]' : large ? 'h-16 w-16 text-[36px]' : 'h-14 w-14 text-[26px]';
  return (
    <button
      onClick={onClick}
      data-testid={`badge-${a.id}`}
      className={['relative flex flex-col items-center gap-1 rounded-[12px] p-2 transition',
        a.unlocked ? 'bg-white border border-tf-border active:scale-95' : 'bg-tf-surface-1 opacity-60'
      ].join(' ')}
    >
      <div className={['flex items-center justify-center rounded-full', size, a.unlocked ? '' : 'grayscale'].join(' ')} style={{ background: a.unlocked ? `linear-gradient(135deg, ${r.color} 0%, ${r.color}99 100%)` : '#C7C7CC', boxShadow: a.unlocked ? `0 4px 12px ${r.glow}` : 'none' }}>
        <span style={{ filter: a.unlocked ? 'none' : 'grayscale(1)' }}>{a.emoji}</span>
      </div>
      {!small && (
        <div className="text-center w-full">
          <div className="text-[10px] font-semibold text-tf-text-1 truncate w-full">{a.name}</div>
          {!a.unlocked && a.progress !== undefined && (
            <div className="mt-0.5 h-1 w-full overflow-hidden rounded-full bg-[#E5E5EA]">
              <div className="h-full rounded-full" style={{ width: a.progress + '%', backgroundColor: r.color }} />
            </div>
          )}
        </div>
      )}
      {a.pinned && !small && <span className="absolute top-1 right-1 text-[10px]">📌</span>}
    </button>
  );
}
